import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIdentity } from './IdentityContext';
import { useConfirm } from './ConfirmContext';
import { useToast } from './ToastContext';
import Spinner from './Spinner';
import { formatPrice } from '../data/price';
import { GOLD, GOLD_LT, CREAM } from '../theme';
import CoverImageManager from './CoverImageManager';
import CategoryPreviewPane from './CategoryPreviewPane';
import PublishBar from './PublishBar';

const emptyForm = { name: '', description: '' };

const inputStyle = { width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(201,162,75,.35)', color: CREAM, fontFamily: "'Cormorant Garamond', serif", fontSize: 18, padding: '10px 2px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 8 };

export default function CategoryForm({ mode }) {
  const { categorySlug: routeSlug } = useParams();
  const { authFetch } = useIdentity();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null); // full draft category incl. coverImage/dresses (edit mode only)
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(mode === 'edit');
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deletingDressSlug, setDeletingDressSlug] = useState(null);

  const load = useCallback(() => {
    if (mode !== 'edit') return undefined;
    setLoading(true);
    return authFetch('/.netlify/functions/categories-draft')
      .then((r) => r.json())
      .then((doc) => {
        const found = (doc.categories || []).find((c) => c.slug === routeSlug);
        if (!found) {
          setNotFound(true);
          return;
        }
        setCategory(found);
        setForm({ name: found.name, description: found.description });
      })
      .finally(() => setLoading(false));
  }, [authFetch, mode, routeSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setSaveError('Name is required');
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      if (mode === 'new') {
        const res = await authFetch('/.netlify/functions/categories-draft', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to create category');
        toast.success('Category created.');
        navigate(`/admin/categories/${data.category.slug}`);
        return;
      }
      const res = await authFetch('/.netlify/functions/categories-draft', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: routeSlug, ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setCategory(data.category);
      toast.success('Draft saved.');
    } catch (err) {
      toast.error(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDress = async (dressSlug) => {
    const ok = await confirm('Delete this piece from the draft? This cannot be undone.');
    if (!ok) return;
    setDeletingDressSlug(dressSlug);
    try {
      const res = await authFetch('/.netlify/functions/categories-dresses', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categorySlug: routeSlug, dressSlug }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete');
      const updated = data.doc.categories.find((c) => c.slug === routeSlug);
      if (updated) setCategory(updated);
      toast.success('Piece deleted from the draft.');
    } catch (err) {
      toast.error(err.message || 'Failed to delete.');
    } finally {
      setDeletingDressSlug(null);
    }
  };

  if (loading) return <p style={{ color: 'rgba(239,228,210,.6)' }}>Loading…</p>;
  if (notFound) return <p style={{ color: '#e08a8a' }}>Category not found in the draft.</p>;

  const previewCategory = { slug: routeSlug || 'preview', ...form, coverImage: category ? category.coverImage : null, dresses: category ? category.dresses : [] };
  const dresses = ((category ? category.dresses : []) || []).slice().sort((a, b) => a.order - b.order);

  return (
    <div>
      <Link to="/admin/categories" style={{ display: 'inline-block', marginBottom: 16, fontSize: 13, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, textDecoration: 'none' }}>← All Categories</Link>
      <h1 style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(26px,3.2vw,36px)', margin: '0 0 24px' }}>
        {mode === 'new' ? 'New Category' : form.name || routeSlug}
      </h1>

      <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 380px', minWidth: 0, maxWidth: 480 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={form.name} onChange={updateField('name')} />
          </div>
          {mode === 'edit' && (
            <div style={{ marginBottom: 20, fontSize: 13, color: 'rgba(239,228,210,.45)' }}>Slug: {routeSlug} (locked)</div>
          )}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }} value={form.description} onChange={updateField('description')} />
          </div>

          {saveError && <p style={{ color: '#e08a8a', fontSize: 14 }}>{saveError}</p>}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', padding: '14px 32px', background: GOLD, color: '#16110c', border: 'none', cursor: saving ? 'default' : 'pointer', opacity: saving ? .6 : 1 }}
          >
            {saving && <Spinner size={14} />}
            {saving ? 'Saving…' : 'Save Draft'}
          </button>

          {mode === 'edit' && category && (
            <>
              <div style={{ marginTop: 40 }}>
                <CoverImageManager category={category} onChange={setCategory} />
              </div>

              <div style={{ marginTop: 40 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h3 style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 20, margin: 0 }}>Pieces</h3>
                  <Link to={`/admin/categories/${routeSlug}/dresses/new`} style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD_LT, textDecoration: 'none' }}>+ Add Piece</Link>
                </div>
                {dresses.length === 0 && <p style={{ fontSize: 13, color: 'rgba(239,228,210,.45)' }}>No pieces yet.</p>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {dresses.map((d) => (
                    <div key={d.slug} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(201,162,75,.25)', padding: '10px 14px' }}>
                      <div>
                        <div style={{ fontSize: 15 }}>{d.name}</div>
                        <div style={{ fontSize: 12, color: 'rgba(239,228,210,.5)' }}>
                          {d.price ? formatPrice(d.price) : 'No price — Enquire'} · {(d.images || []).length} image{(d.images || []).length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <Link to={`/admin/categories/${routeSlug}/dresses/${d.slug}`} style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD_LT, textDecoration: 'none' }}>Edit</Link>
                        <button onClick={() => handleDeleteDress(d.slug)} disabled={deletingDressSlug === d.slug} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: '#e08a8a', background: 'none', border: 'none', cursor: deletingDressSlug === d.slug ? 'default' : 'pointer', opacity: deletingDressSlug === d.slug ? .6 : 1, padding: 0 }}>
                          {deletingDressSlug === d.slug && <Spinner size={11} />}
                          {deletingDressSlug === d.slug ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {mode === 'new' && (
            <p style={{ marginTop: 24, fontSize: 13, color: 'rgba(239,228,210,.45)' }}>Save the category first to add a cover image and pieces.</p>
          )}
        </div>

        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          <CategoryPreviewPane category={previewCategory} />
        </div>
      </div>

      <div style={{ marginTop: 40 }}>
        <PublishBar endpoint="/.netlify/functions/categories-publish" />
      </div>
    </div>
  );
}
